var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import InstancePlugin from "../../mixin/InstancePlugin.js";
import Menu from "../../widget/Menu.js";
import Objects from "../../helper/util/Objects.js";
import BrowserHelper from "../../helper/BrowserHelper.js";
import DomHelper from "../../helper/DomHelper.js";
import StringHelper from "../../helper/StringHelper.js";
import EventHelper from "../../helper/EventHelper.js";
import ObjectHelper from "../../helper/ObjectHelper.js";
import ArrayHelper from "../../helper/ArrayHelper.js";
let contextMenuKeyEvent;
class ContextMenuBase extends InstancePlugin {
  //region Config
  static get configurable() {
    return {
      /**
       * This is a type of the context menu used to generate correct names for methods and events.
       * Should be in camel case. Required to be set in subclass.
       * @config {String}
       * @readonly
       */
      type: null,
      /**
       * Gets the Menu instance that this feature is using.
       * @member {Core.widget.Menu} menu
       * @readonly
       */
      /**
       * A config which will be applied when creating the Menu component.
       * @config {MenuConfig}
       */
      menu: {
        $config: ["lazy", "nullify"],
        value: {
          type: "menu",
          autoShow: false,
          closeAction: "hide",
          constrainTo: globalThis,
          scrollAction: null
        }
      },
      /**
       * {@link @bryntum/core-thin/lib/widget/Menu} items object containing named child menu items to apply to the feature's
       * provided context menu.
       *
       * This may add extra items as below, but may also remove any of the default items by configuring the name
       * of the item as `null`.
       *
       * ```javascript
       * features : {
       *     cellMenu : {
       *         // This object is applied to the Feature's predefined default items
       *         items : {
       *             switchToDog : {
       *                 text : 'Dog',
       *                 icon : 'b-fa b-fa-fw b-fa-dog',
       *                 onItem({record}) {
       *                     record.dog = true;
       *                     record.cat = false;
       *                 },
       *                 weight : 500     // Make this second from end
       *             },
       *             switchToCat : {
       *                 text : 'Cat',
       *                 icon : 'b-fa b-fa-fw b-fa-cat',
       *                 onItem({record}) {
       *                     record.dog = false;
       *                     record.cat = true;
       *                 },
       *                 weight : 510     // Make this sink to end
       *             },
       *             add : null // We do not want the "Add" submenu to be available
       *         }
       *     }
       * }
       * ```
       *
       * @config {Object<String,MenuItemConfig|Boolean|null>}
       */
      items: {},
      /**
       * Event which is used to show context menu.
       * Available options are: 'contextmenu', 'click' and 'dblclick'.
       * Default value is used from {@link @bryntum/grid-thin/lib/view/GridBase#config-contextMenuTriggerEvent}
       * @config {'contextmenu'|'click'|'dblclick'|null}
       */
      triggerEvent: null,
      /**
       * A CSS selector targeting an element, such as an ellipsis icon that when
       * clicked will trigger the menu to show.
       * @config {String}
       */
      clickTriggerSelector: null,
      /**
       * Set to `true` to prevent the native menu from showing when there are no menu items to show, or
       * you manually prevent the menu from showing in an event listener.
       * @prp {Boolean}
       */
      preventNativeMenu: null,
      /**
       * See {@link #keyboard-shortcuts Keyboard shortcuts} for details
       * @config {Object<String,KeyMapConfig>}
       */
      keyMap: {
        " ": { handler: "showContextMenuByKey", weight: 100 },
        "Ctrl+Space": "showContextMenuByKey"
      }
    };
  }
  // Plugin configuration. This plugin chains some of the functions in Grid.
  // The contextmenu event is emulated from a taphold gesture on touch platforms.
  static get pluginConfig() {
    return {
      assign: ["showContextMenu"],
      chain: [
        "onElementContextMenu",
        "onElementClick",
        "onElementDblClick"
      ]
    };
  }
  //endregion
  //region Init
  construct(...args) {
    var _a;
    super.construct(...args);
    if (!((_a = this.type) == null ? void 0 : _a.length)) {
      throw new Error(`Config 'type' is required to be specified for context menu`);
    }
  }
  //endregion
  //region Events
  /**
   * This event fires on the owning widget when an item is selected in the context menu.
   * @event contextMenuItem
   * @on-owner
   * @param {Core.widget.Widget} source The owning widget
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   */
  /**
   * This event fires on the owning widget when a check item is toggled in the context menu.
   * @event contextMenuToggleItem
   * @on-owner
   * @param {Core.widget.Widget} source The owning widget
   * @param {Core.widget.Menu} menu The menu
   * @param {Core.widget.MenuItem} item Selected menu item
   * @param {Boolean} checked Checked or not
   */
  onElementContextMenu(event) {
    this.onElementEvent(event);
  }
  onElementClick(event) {
    this.onElementEvent(event);
  }
  onElementDblClick(event) {
    this.onElementEvent(event);
  }
  onElementEvent(event) {
    if (!event.handled && this.triggerEvent === event.type) {
      if (event.ctrlKey === true && !BrowserHelper.isMac) {
        event.preventDefault();
        return;
      }
      const originalEvent = event;
      if (contextMenuKeyEvent) {
        const { target } = contextMenuKeyEvent;
        event = EventHelper.fixEvent(new MouseEvent(event.type, event));
        Object.defineProperties(event, {
          target: { value: target },
          offsetX: { value: originalEvent.offsetX },
          offsetY: { value: originalEvent.offsetY }
        });
        event.preventDefault = function() {
          originalEvent.preventDefault();
          originalEvent.handled = true;
        };
      }
      this.internalShowContextMenu(event);
    } else if (event.type === "click" && this.clickTriggerSelector && event.target.matches(this.clickTriggerSelector)) {
      this.internalShowContextMenu(event, { align: "l-r", target: event.target });
    }
  }
  showContextMenuByKey(event) {
    if (!DomHelper.isEditable(event.target)) {
      const { target } = event;
      if (target) {
        contextMenuKeyEvent = event;
        DomHelper.triggerMouseEvent(target, this.triggerEvent);
        event.handled = true;
        event.preventDefault();
        event.stopImmediatePropagation();
        contextMenuKeyEvent = null;
        return true;
      }
    }
    return false;
  }
  //endregion
  //region Menu handlers
  internalShowContextMenu(domEvent, alignSpec) {
    const me = this;
    let data;
    if (me.disabled) {
      return;
    }
    if (!domEvent.handled && !domEvent.defaultPrevented && (data = me.getDataFromEvent(domEvent)) && me.shouldShowMenu(data)) {
      if (domEvent.type === "contextmenu" && BrowserHelper.isSafari && !domEvent.ctrlKey && domEvent.metaKey) {
        EventHelper.on({
          element: domEvent.target,
          capture: true,
          once: true,
          thisObj: this,
          click(e) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        });
      }
      data.domEvent = domEvent;
      me.showContextMenu(data, alignSpec);
    }
  }
  getDataFromEvent(event) {
    return {
      feature: this,
      event,
      targetElement: this.getTargetElementFromEvent(event)
    };
  }
  getTargetElementFromEvent(event) {
    return event.target;
  }
  /**
   * Shows the context menu.
   * @param {Event} event The initiating event.
   * @param {AlignSpec|HTMLElement} [alignSpec] Menu alignment specification, or an element to align to
   * @on-owner
   */
  async showContextMenu(event, alignSpec) {
    var _a, _b;
    const me = this, isDOMEvent = DomHelper.isDOMEvent(event), menuContext = isDOMEvent ? me.getDataFromEvent(event) : event, domEvent = isDOMEvent ? event : menuContext.domEvent;
    (_a = me._menu) == null ? void 0 : _a.hide();
    if (me.disabled) {
      return;
    }
    me.menuContext = menuContext;
    const {
      type,
      client,
      processItems
    } = me, elCenter = DomHelper.isInView(menuContext.targetElement).center;
    Objects.assign(menuContext, {
      point: (domEvent == null ? void 0 : domEvent.clientX) ? [domEvent.clientX + 1, domEvent.clientY + 1] : [elCenter.x, elCenter.y],
      menu: me,
      items: {},
      selection: client.selectedRecords
    });
    me.callChainablePopulateMenuMethod(menuContext);
    Objects.merge(menuContext.items, me.baseItems);
    if (me.preventNativeMenu) {
      me.preventDefaultEvent(menuContext);
    }
    if ((!processItems || await me.callback(processItems, client, [menuContext]) !== false) && me.hasActiveMenuItems(menuContext)) {
      me.populateItemsWithData(menuContext);
      if (me.beforeContextMenuShow(menuContext) !== false) {
        const { menu } = me, { featureItems } = menu, { items } = menuContext;
        me.preventDefaultEvent(menuContext);
        let result = client.trigger(`${type}MenuBeforeShow`, menuContext);
        if (ObjectHelper.isPromise(result)) {
          domEvent.handled = true;
          result = await result;
        }
        if (!Object.values(items).some((val) => val)) {
          return;
        }
        if (result !== false) {
          if (featureItems) {
            menu.remove(featureItems);
            ArrayHelper.asArray(featureItems).forEach((i) => i.destroy());
          }
          menu.featureItems = menu.add(...Array.isArray(items) ? items : menu.processItemsObject(items));
          menu.showBy(alignSpec || {
            target: menuContext.domEvent,
            align: "s0-e100"
          });
          (_b = me.touchMoveDetacher) == null ? void 0 : _b.call(me);
          me.touchMoveDetacher = EventHelper.on({
            element: client.element,
            touchmove: () => menu.hide(),
            thisObj: menu,
            once: true
          });
          if (domEvent) {
            domEvent.preventDefault();
            domEvent.handled = true;
          }
        }
      }
    }
  }
  /**
   * Returns the base, configured-in menu items set from the configured items, taking into
   * account the namedItems the feature offers.
   * @property {Object[]}
   * @readonly
   * @internal
   */
  get baseItems() {
    if (!this._baseItems) {
      const me = this, { namedItems } = me, baseItems = me._baseItems = Objects.assign({}, me.items);
      for (const ref in baseItems) {
        const item = baseItems[ref];
        if (item) {
          if (namedItems && ref in namedItems) {
            baseItems[ref] = typeof item === "object" ? Objects.merge(Objects.clone(namedItems[ref]), item) : namedItems[ref];
          } else if (item === true) {
            delete baseItems[ref];
          }
        }
      }
    }
    return this._baseItems;
  }
  /**
   * Hides the context menu
   * @internal
   */
  hideContextMenu(animate) {
    var _a;
    (_a = this.menu) == null ? void 0 : _a.hide(animate);
  }
  callChainablePopulateMenuMethod(eventParams) {
    var _a, _b;
    (_b = (_a = this.client)[`populate${StringHelper.capitalize(this.type)}Menu`]) == null ? void 0 : _b.call(_a, eventParams);
  }
  hasActiveMenuItems(eventParams) {
    return Object.values(eventParams.items).some((item) => item && !item.hidden);
  }
  /**
   * Override this function and return `false` to prevent the context menu from being shown. Returns `true` by default.
   * Returning false will by default make the native OS menu show, unless you configure {@link #config-preventNativeMenu}
   * @returns {Boolean}
   * @internal
   */
  shouldShowMenu() {
    return true;
  }
  beforeContextMenuShow(eventParams) {
  }
  populateItemsWithData(eventParams) {
  }
  preventDefaultEvent(eventParams) {
    var _a;
    (_a = eventParams.event) == null ? void 0 : _a.preventDefault();
  }
  //endregion
  //region Configurables
  get triggerEvent() {
    return this._triggerEvent || this.client.contextMenuTriggerEvent;
  }
  changeMenu(menu, oldMenu) {
    const me = this, {
      client,
      type
    } = me;
    if (menu) {
      return Menu.reconfigure(oldMenu, menu ? Menu.mergeConfigs({
        owner: client,
        rootElement: client.rootElement,
        onItem(itemEvent) {
          client.trigger(`${type}MenuItem`, itemEvent);
        },
        onToggle(itemEvent) {
          client.trigger(`${type}MenuToggleItem`, itemEvent);
        },
        onDestroy() {
          me.menu = null;
        },
        // Load up the item event with the contextual info
        onBeforeItem(itemEvent) {
          Object.assign(itemEvent, me.menuContext);
        },
        onShow({ source: menu2 }) {
          me.menuContext.menu = menu2;
          client.trigger(`${type}MenuShow`, me.menuContext);
        }
      }, menu) : null, me);
    } else if (oldMenu == null ? void 0 : oldMenu.isWidget) {
      oldMenu.destroy();
    }
  }
  //endregion
}
__publicField(ContextMenuBase, "$name", "ContextMenuBase");
ContextMenuBase._$name = "ContextMenuBase";
export {
  ContextMenuBase as default
};
